import b from 'b_';
import { Close as IconClose } from 'education-icons';
import { Button, Image, SimpleText, Text, useIntl, useStaticUrls } from 'platform-components';
import { TooltipStateful } from 'platform-components/src/components/LegoTooltip/LegoTooltip';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import ym from 'react-yandex-metrika';

import * as certificatesModalSelectors from 'common.components/certificate-modal/selectors';
import CircleProgressBar from 'common.components/circle-progress-bar/circle-progress-bar';
import { addViewedHint } from 'common.components/user/actions';
import { getAssignedLessonsCount, getIsTeacherCertBannerWasClosed } from 'common.components/user/selectors';
import {
    LESSONS_ASSIGNED_TO_GET_CERTIFICATE,
    TEACHER_CERT_BANNER_WAS_CLOSED,
} from 'common.components/user/user.constants';

import { TEACHER_CERTIFICATE_SLUG } from '../../../constants/certificates';
import { getShouldShowCertificateBanner } from '../selectors/notification.selectors';

import './course-view__certificate-banner.scss';

enum CERT_STATUSES {
    ALMOST_COMPLETED = 'almostCompleted',
    COMPLETED = 'completed',
    IN_PROGRESS = 'inProgress',
}

/* eslint-disable complexity */
export const CourseViewCertificateBanner = () => {
    const intl = useIntl();
    const wasTeacherCertBannerClosed = useSelector(getIsTeacherCertBannerWasClosed);
    const dispatch = useDispatch();
    const history = useHistory();
    const { labPortfolio } = useStaticUrls();

    const lessonsAssigned = useSelector(getAssignedLessonsCount);
    const assigned = Math.min(lessonsAssigned, LESSONS_ASSIGNED_TO_GET_CERTIFICATE);

    const shouldShowBanner = useSelector((store) => getShouldShowCertificateBanner(store));
    const shouldShowCertificateModal = useSelector((store) =>
        certificatesModalSelectors.shouldShowBannerCertificateModal(store, { slug: TEACHER_CERTIFICATE_SLUG })
    );
    const hasAgreedToReceiveCertificate = useSelector((store) =>
        certificatesModalSelectors.getHasAgreedToReceiveCertificate(store, { slug: TEACHER_CERTIFICATE_SLUG })
    );
    const almostCompleted = lessonsAssigned >= LESSONS_ASSIGNED_TO_GET_CERTIFICATE;
    const isCompleted = Boolean(shouldShowCertificateModal);

    const certStatus =
        (isCompleted && CERT_STATUSES.COMPLETED) ||
        (almostCompleted && CERT_STATUSES.ALMOST_COMPLETED) ||
        CERT_STATUSES.IN_PROGRESS;

    const titleId = `innovatorTeacherCertificate.banner.title.${certStatus}`;
    const descriptionId = `innovatorTeacherCertificate.banner.description.v2.${certStatus}`;

    const goToPortfolio = () => {
        ym('reachGoal', 'teacher_innovator_cert_get_click');

        history.push(labPortfolio);
    };

    const stopShowCertBanner = () => {
        dispatch(addViewedHint(TEACHER_CERT_BANNER_WAS_CLOSED, true));
    };

    if (!shouldShowBanner || wasTeacherCertBannerClosed || hasAgreedToReceiveCertificate) {
        return null;
    }

    return (
        <>
            <div className={b('course-view', 'certificate-banner', { completed: isCompleted })}>
                {isCompleted && (
                    <IconClose className={b('course-view', 'certificate-banner-close')} onClick={stopShowCertBanner} />
                )}
                <Image
                    alt={intl.formatMessage({ id: 'innovatorTeacherCertificate.banner.imgAlt' })}
                    className="course-view__certificate-banner-img"
                    src={intl.formatMessage({ id: 'innovatorTeacherCertificate.banner.imgSrc' })}
                />
                <div className="course-view__certificate-banner-msg">
                    <div className="course-view__certificate-banner-title">
                        <Text id={titleId} size={Text.SIZE.XL} weight={Text.WEIGHT.M} />
                    </div>
                    <div className="course-view__certificate-banner-description">
                        <Text id={descriptionId} type={Text.TYPE.HTML} />
                    </div>
                </div>
                {isCompleted ? (
                    <Button
                        className={b('course-view', 'certificate-banner-button')}
                        onClick={goToPortfolio}
                        size={Button.SIZE.M}
                        transparencyTheme={Button.TRANSPARENCY_THEME.DARK}
                        view={Button.VIEW.PSEUDO}
                    >
                        <SimpleText id="innovatorTeacherCertificate.banner.btnText" />
                    </Button>
                ) : (
                    <div className="course-view__certificate-banner-progress-bar">
                        <TooltipStateful
                            className="course-view__certificate-banner-tooltip"
                            content={intl.formatMessage(
                                { id: 'innovatorTeacherCertificate.banner.tooltip' },
                                {
                                    assigned,
                                    max: LESSONS_ASSIGNED_TO_GET_CERTIFICATE,
                                }
                            )}
                            direction={['top', 'right']}
                            hasTail={true}
                            size="s"
                            trigger={['hover', 'click']}
                            view="default"
                        >
                            <div>
                                <CircleProgressBar
                                    maxParts={LESSONS_ASSIGNED_TO_GET_CERTIFICATE}
                                    passedParts={assigned}
                                />
                            </div>
                        </TooltipStateful>
                    </div>
                )}
            </div>
        </>
    );
};
